import { useState } from "react"
import { formatCurrency } from "src/shared/utils/formatCurrency"
import "./segments.scss"
import { D, Flight, Segment, SpendPattern, fmtDay, pct2 } from "./segmentEngine"
import { keyDates } from "./keyDates"
import { SegmentStrip, InfoTip } from "./SegmentStrip"
import { SegmentNetwork, SegmentNetworksTable } from "./SegmentNetworksTable"
import { BudgetConfirmDialog, PauseConfirmDialog } from "./SaveDialogs"
import { LogEntry, useBudgetDraft } from "./useBudgetDraft"
import { BudgetStatBar } from "./BudgetStatBar"
import { BudgetNote, NotePanel, NotesList } from "./BudgetNotes"
import { CompletionOverlay } from "./CompletionOverlay"
import { TodoKind, todo } from "./completion"

const PATTERN_LABEL: Record<SpendPattern, string> = {
  even: "Even",
  monthly: "Monthly",
  custom: "Custom",
}

export type CampaignSegmentBudgetProps = {
  campaignName: string
  /** The campaign's share of the engagement, set as a percent on the Campaigns list. */
  allocation: number
  savedMargin: number
  savedPattern: SpendPattern
  flight: Flight
  windowStart: string
  windowEnd: string
  fundedDays: number
  /** The campaign's serving ranges, for the divisor on segment-wide figures. */
  scheduledRanges: [string, string][]
  /** ISO date of first publish, or null while the start is still an estimate (MPN-2040). */
  goLiveDate?: string | null
  /** Takes the operator to the Campaign page, where the dates are actually edited. */
  onEditCampaign?: () => void
  onGoToPublish?: () => void
  daysRemainingLabel: string
  nextBillingLabel: string
  nextBillingDate?: string | null
  nextReplenishDate?: string | null
  billingMethod: string
  /** PFR / subscription note shown beside Amount, as the prototype's budgetType tag. */
  budgetTypeNote?: string | null
  networks: SegmentNetwork[]
  defaultMix: Record<string, number>
  today: string
  configured: boolean
  /** Saved segments for the active pattern; omitted, it is seeded from scratch. */
  initialSegments?: Segment[]
  /** Back to the campaign list, from the breadcrumb. */
  onBack: () => void
  /** The engagement's own figures, shown between the crumb bar and the campaign stats. */
  engagement: {
    balance: number
    daysRemaining: string
    nextBillingCycle: string
    engagementSpend: number
  }
  /** Persist the draft. Rejecting keeps the screen in Edit with the draft intact. */
  onCommit?: (
    entries: LogEntry[],
    segments: Segment[],
    pattern: SpendPattern,
    marginPct: number,
    /** Daily Cap edits by network key. Campaign-wide, so they sit beside the windows
     *  rather than inside one (MPN-1806). */
    capEdits: Record<string, string>,
  ) => void | Promise<void>
}

export function CampaignSegmentBudget(props: CampaignSegmentBudgetProps) {
  const [view, setView] = useState<"cards" | "timeline">("cards")

  /* Every save that changes something logs an entry; the note on top of it is optional, and
     is offered once for the first entry of that save (MPN-1807). Local for now. */
  const [log, setLog] = useState<BudgetNote[]>([])
  const [notesOpen, setNotesOpen] = useState(false)
  const [notePanel, setNotePanel] = useState<BudgetNote | null>(null)
  const written = log.filter((n) => n.text || n.tag).length

  const draft = useBudgetDraft({
    flight: props.flight,
    allocation: props.allocation,
    savedMargin: props.savedMargin,
    savedPattern: props.savedPattern,
    networks: props.networks,
    defaultMix: props.defaultMix,
    fundedDays: props.fundedDays,
    today: props.today,
    configured: props.configured,
    initialSegments: props.initialSegments,
    onCommit: async (entries, store, pattern) => {
      // The server has to take it before anything is written to the on-screen log --
      // otherwise a rejected save leaves a history entry for a change that never happened.
      await props.onCommit?.(entries, store[pattern], pattern, draft.marginPct, draft.capEdits)
      if (entries.length) {
        const stamped = entries.map((e, i) => ({
          id: Date.now() + i,
          kind: e.kind,
          summary: e.summary,
          ts: new Date(),
        }))
        setLog((l) => l.concat(stamped))
        setNotePanel(stamped[0])
      }
    },
  })

  const marks = keyDates({
    flight: props.flight,
    windowStart: props.windowStart,
    windowEnd: props.windowEnd,
    nextBillingDate: props.nextBillingDate,
    nextReplenishDate: props.nextReplenishDate,
  })

  const blocked = draft.floorFails.length > 0

  const segmentCount =
    draft.active === "even" ? "one spend period" : `${draft.store[draft.active].length} spend segments`

  return (
    <div className="seg-budget">
      {/* Production puts the campaign breadcrumb above the engagement figures, and hangs the
          page's own actions off it rather than off the campaign stat row. */}
      <div className="seg-crumb">
        <button type="button" className="seg-crumb__back" onClick={props.onBack}>
          &lt; Campaigns
        </button>
        <span className="seg-crumb__sep">/</span>
        <span className="seg-crumb__name">{props.campaignName}</span>
        <span className="seg-crumb__meta">
          Spend Pattern: {PATTERN_LABEL[draft.active]} · {segmentCount}
        </span>

        <span className="seg-crumb__actions">
          <button
            type="button"
            className="ds-btn ds-btn--sm ds-btn--secondary"
            onClick={() => setNotesOpen(true)}
            {...todo("draft", "Entries are React state. No durable change log until MPN-1807.")}
          >
            Notes{written ? ` · ${written}` : ""}
          </button>
          {/* The full reason lives in the panel below; this line cannot wrap. */}
          {draft.editing && blocked && <span className="seg-crumb__blocked">! Below daily minimum</span>}
          {draft.editing ? (
            <>
              {/* Not in the prototype, which toggles Edit straight into Save. Kept as the
                  escape hatch from a draft: Save is blockable, so without it a failing floor
                  leaves no way out but discarding the tab. */}
              <button type="button" className="ds-btn ds-btn--sm ds-btn--secondary" onClick={draft.cancelEdit}>
                Cancel
              </button>
              <span title={blocked ? floorTitle(draft.floorFails) : undefined}>
                <button
                  type="button"
                  className="ds-btn ds-btn--sm ds-btn--primary"
                  disabled={blocked}
                  onClick={draft.save}
                  {...todo("draft", "Runs every confirm, then discards. No endpoint yet — MPN-1802.")}
                >
                  Save
                </button>
              </span>
            </>
          ) : (
            <button type="button" className="ds-btn ds-btn--sm ds-btn--secondary" onClick={draft.beginEdit}>
              Edit
            </button>
          )}
        </span>
      </div>

      <BudgetStatBar
        balance={props.engagement.balance}
        daysRemaining={props.engagement.daysRemaining}
        nextBillingCycle={props.engagement.nextBillingCycle}
        engagementSpend={props.engagement.engagementSpend}
        today={props.today}
      />

      <div className="seg-budget__body">

      <div className="seg-budget__stats">
        <MiniStat label="This Campaign" value={props.campaignName} wide />
        <MiniStat
          label="Amount"
          value={formatCurrency(draft.netAmount)}
          tagNote={props.budgetTypeNote}
          formula={`Amount = Allocation × (1 − Margin) = ${formatCurrency(props.allocation)} × ${pct2(100 - draft.marginPct)}%`}
          method="Allocation is this campaign's share of the engagement's remaining flight funding, set as a percent on the Campaigns list behind the breadcrumb above. Amount can never exceed it."
          // Surfaced inline rather than hover-only: Balance and Amount sit next to each
          // other and nothing otherwise says margin applies to this campaign's Allocation.
          note={`${pct2(100 - draft.marginPct)}% of ${formatCurrency(props.allocation)} Allocation`}
        />
        <MiniStat
          label="Margin"
          value={`${pct2(draft.marginPct).replace(/\.00$/, "")}%`}
          /* Two states (MPN-1803). On a legacy campaign margin is a holdback and nothing
             else; on a segmented one it also drives Amount, and every upcoming window
             reflows when it moves. Saying the same sentence for both understates what an
             edit does on the screen where it does the most. */
          info={
            props.configured
              ? "Drives Amount — upcoming segments reflow to match."
              : "Contract dollars held back from media."
          }
          formula={`Amount = ${formatCurrency(props.allocation)} × (1 − ${pct2(draft.marginPct)}%) = ${formatCurrency(draft.netAmount)}`}
          method={
            draft.marginPct > 0
              ? "Editing Margin moves real money. Current and upcoming segments reflow to match; elapsed segments keep their dollars."
              : "At 0%, the whole allocation goes to media."
          }
          todoKind={draft.editing ? "draft" : undefined}
          todoNote="Reflows all three patterns correctly, then discards on refresh."
          control={
            draft.editing ? (
              <MarginField value={draft.marginPct} onChange={draft.setMargin} />
            ) : undefined
          }
        />
        <MiniStat
          label="Spend Pattern"
          value={
            <span className="seg-budget__pattern">
              <span>{PATTERN_LABEL[draft.active]}</span>
              <span className="seg-active-tag">Active</span>
              {draft.editing && draft.draftActive !== draft.active && (
                <span className="seg-budget__pattern-next">→ {PATTERN_LABEL[draft.draftActive]} on save</span>
              )}
            </span>
          }
        />
        <MiniStat
          label="Billing Method"
          value={props.billingMethod}
          todoKind={props.billingMethod === "—" ? "data" : undefined}
          todoNote="Contract.billing_method is blank on 61 of 108 contracts."
        />

      </div>

      {/*
          The campaign's dates, read-only. They are set on the Campaign page and are the
          single source of truth for them (MPN-2040) — this screen only orients against
          them, which is why both carry a lock and the strip ends in a way out.

          Start is an ESTIMATE until the campaign first publishes. At that point it becomes
          the date it actually went live and stops being editable, so the label changes with
          it rather than calling an estimate and a fact the same thing.
      */}
      <div className="seg-budget__stats">
        {props.goLiveDate ? (
          <MiniStat label="Went live" value={<LockedDate day={props.goLiveDate} />} />
        ) : (
          <MiniStat
            label="Est. start"
            value={<span className="seg-budget__est">{fmtDay(props.flight.start)}</span>}
          />
        )}
        <MiniStat label="Campaign end" value={<LockedDate day={props.flight.end} />} />
        <MiniStat label="Days Remaining" value={`${props.daysRemainingLabel} in cycle`} />
        <MiniStat label="Next Billing" value={props.nextBillingLabel} />
        <div className="seg-budget__stats-link">
          <a
            href="#"
            className="seg-link"
            onClick={(e) => {
              e.preventDefault()
              props.onEditCampaign?.()
            }}
          >
            Edit on Campaign ›
          </a>
        </div>
      </div>

      {/*
          Saving does not reach the networks. Every edit on this screen waits for Publish or
          the overnight run, and nothing else on the page says so -- without this line an
          operator reasonably reads a saved segment as a live one.
      */}
      <div className="seg-publish-note">
        <span>Changes go out on Publish or the next nightly run.</span>
        <a
          href="#"
          className="seg-link"
          onClick={(e) => {
            e.preventDefault()
            props.onGoToPublish?.()
          }}
        >
          Go to Publish ›
        </a>
      </div>

      {/* A floor failure blocks Save outright: below it the network will not serve at all. */}
      {draft.editing && blocked && (
        <div className="seg-floor">
          <span className="seg-floor__bang">!</span>
          <div>
            <div>
              <strong>Save is blocked.</strong> Raise the daily amount, change the spend pattern, or pause
              the network or ad group.
            </div>
            {/* One line per offender with its own floor, so the fix is readable without
                opening each row. */}
            <ul>
              {draft.floorFails.map((f, i) => (
                <li key={i}>
                  <strong>{f.who}:</strong> {formatCurrency(f.rate)} of {formatCurrency(f.min)}/day minimum
                  <span className="seg-floor__where"> · {f.segment}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <SegmentStrip
        today={props.today}
        segments={draft.segments}
        selectedId={draft.selectedId}
        onSelect={draft.selectSegment}
        editing={draft.editing}
        mode={draft.mode}
        onMode={draft.setMode}
        active={draft.active}
        draftActive={draft.draftActive}
        onSetActive={draft.setActivePattern}
        view={view}
        onView={setView}
        netAmount={draft.netAmount}
        flight={props.flight}
        windowStart={props.windowStart}
        windowEnd={props.windowEnd}
        fundedDays={props.fundedDays}
        scheduledRanges={props.scheduledRanges}
        keyDates={marks}
        onUpdate={draft.updateSegment}
        onDelete={draft.removeSegment}
        onAdd={draft.addSegment}
        onAddAt={draft.addSegmentAt}
      />

      <div className="seg-budget__table">
        <SegmentNetworksTable
          // Dollars already spent. The table reads normally; nothing in it can be moved.
          locked={D(draft.selected.end) < D(props.today)}
          segment={draft.selected}
          networks={props.networks}
          defaultMix={props.defaultMix}
          flight={props.flight}
          fundedDays={props.fundedDays}
          scheduledRanges={props.scheduledRanges}
          editing={draft.editing}
          pendingNet={draft.pendingNet}
          pendingAg={draft.pendingAg}
          onSetPercent={draft.setNetworkPercent}
          onSetSpend={draft.setNetworkSpend}
          onSetCap={draft.setNetworkCap}
          capEdits={draft.capEdits}
          onUnpin={draft.unpin}
          onToggleNetwork={draft.toggleNetwork}
          onToggleAdGroup={draft.toggleAdGroup}
        />
      </div>

      {draft.confirm?.kind === "budget" && (
        <BudgetConfirmDialog
          margin={draft.confirm.margin}
          segments={draft.confirm.segments}
          onCancel={draft.cancelConfirm}
          onConfirm={draft.confirmBudget}
        />
      )}

      </div>

      <CompletionOverlay />

      {notesOpen && <NotesList notes={log} onClose={() => setNotesOpen(false)} />}

      {notePanel && (
        <NotePanel
          summary={notePanel.summary}
          onSkip={() => setNotePanel(null)}
          onSave={(text, tag) => {
            setLog((l) => l.map((n) => (n.id === notePanel.id ? { ...n, text, tag } : n)))
            setNotePanel(null)
          }}
        />
      )}

      {draft.confirm?.kind === "pause" && draft.pauses[draft.confirm.index] && (
        <PauseConfirmDialog
          key={draft.confirm.index}
          pauses={
            draft.pauses[draft.confirm.index].kind === "net"
              ? draft.pauses.filter((p) => p.kind === "net")
              : [draft.pauses[draft.confirm.index]]
          }
          step={draft.pauses.length > 1 ? `${draft.confirm.index + 1} of ${draft.pauses.length}` : null}
          flightEnd={props.flight.end}
          onCancel={draft.cancelConfirm}
          onChoose={draft.choosePause}
        />
      )}
    </div>
  )
}

const floorTitle = (fails: { who: string; rate: number; min: number; segment: string }[]) =>
  "Can't save — below daily minimum:\n" +
  fails.map((f) => `${f.who}: ${formatCurrency(f.rate)} of ${formatCurrency(f.min)}/day (${f.segment})`).join("\n")

function MiniStat({
  label,
  value,
  wide,
  note,
  tagNote,
  info,
  formula,
  method,
  control,
  todoKind,
  todoNote,
}: {
  label: string
  value: React.ReactNode
  wide?: boolean
  note?: string
  tagNote?: string | null
  info?: string
  formula?: string
  method?: string
  control?: React.ReactNode
  todoKind?: TodoKind
  todoNote?: string
}) {
  return (
    <div
      className={`seg-ministat${wide ? " seg-ministat--wide" : ""}`}
      {...(todoKind ? todo(todoKind, todoNote ?? "") : {})}
    >
      <div className="seg-ministat__label">
        {label}
        {(info || formula || method) && <InfoTip info={info} formula={formula} method={method} />}
      </div>
      <div className="seg-ministat__row">
        {control ?? <span className="seg-ministat__value">{value}</span>}
        {tagNote && <InfoTip info={tagNote} />}
      </div>
      {note && <div className="seg-ministat__note">{note}</div>}
    </div>
  )
}

/**
 * Commits on every keystroke rather than on blur, so the Networks table, dailies, totals and
 * the segment media-spend lines all recalculate live as the number is typed.
 */
function MarginField({ value, onChange }: { value: number; onChange: (v: number) => void }) {
  const [draft, setDraft] = useState<string | null>(null)

  return (
    <span className="seg-field">
      <input
        value={draft !== null ? draft : String(value)}
        inputMode="decimal"
        style={{ width: 44 }}
        onChange={(e) => {
          const clean = e.target.value.replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1")
          setDraft(clean)
          const n = parseFloat(clean)
          if (!isNaN(n)) onChange(Math.max(0, Math.min(100, n)))
        }}
        onBlur={() => setDraft(null)}
      />
      <span className="seg-field__prefix">%</span>
    </span>
  )
}

/*
    A date this screen can show but not change. The lock is the whole point: campaign dates
    live on the Campaign page (MPN-2040), and without it a read-only date next to an
    editable Margin reads as a field someone forgot to make editable.
*/
function LockedDate({ day }: { day: string }) {
  return (
    <span className="seg-locked-date">
      {fmtDay(day)}
      <i className="bi bi-lock-fill" title="Set on the Campaign page" />
    </span>
  )
}
